import {ChangeDetectorRef, EventEmitter, Injectable, OnDestroy, Pipe, PipeTransform} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription} from 'rxjs';

import { equals, getTranslateValue, isDefined } from '@core/utils';
import { BelivTranslateService } from '@common/services';

@Injectable()
@Pipe({
    name: 'translateField',
    pure: false // required to update the value when the promise is resolved
    ,
    standalone: false
})
export class TranslateFieldPipe implements PipeTransform, OnDestroy {
  value: String = '';
  lastText: String | null = null;
  lastParams: any[] = [];
  onLangChange: Subscription | undefined;
  onDefaultLangChange: Subscription | undefined;

  constructor(private translate: TranslateService, private _ref: ChangeDetectorRef) {
  }

  updateValue(value: string, params?: any, lang?: string): void {
    this.value = getTranslateValue(value, params, lang);
    this.lastText = value;
    this._ref.markForCheck();
  }

  transform(text: string, ...args: any[]): any {
    if (!text || !text.length) {
      return text;
    }

    // if we ask another time for the same key, return the last value
    if (equals(text, this.lastText) && equals(args, this.lastParams)) {
      return this.value;
    }

    let params: Object | undefined = undefined;
    if (isDefined(args[0]) && args.length) {
      if (typeof args[0] === 'string' && args[0].length) {
        // we accept objects written in the template such as {n:1}, {'n':1}, {n:'v'}
        // which is why we might need to change it to real JSON objects such as {"n":1} or {"n":"v"}
        let validArgs: string = args[0]
          .replace(/(\')?([a-zA-Z0-9_]+)(\')?(\s)?:/g, '"$2":')
          .replace(/:(\s)?(\')(.*?)(\')/g, ':"$3"');
        try {
          params = JSON.parse(validArgs);
        } catch (e) {
          throw new SyntaxError(`Wrong parameter in TranslatePipe. Expected a valid Object, received: ${args[0]}`);
        }
      } else if (typeof args[0] === 'object' && !Array.isArray(args[0])) {
        params = args[0];
      }
    }

    // store the text, in case it changes
    this.lastText = text;

    // store the params, in case they change
    this.lastParams = args;

    // set the value
    this.updateValue(text, params, this.translate.currentLang);

    // if there is a subscription to onLangChange, clean it
    this._dispose();

    // subscribe to onLangChange event, in case the language changes
    if (!this.onLangChange) {
      this.onLangChange = this.translate.onLangChange.subscribe((event) => {
        if (this.lastText) {
          this.lastText = null; // we want to make sure it doesn't return the same value until it's been updated
          this.updateValue(text, params, event.lang);
        }
      });
    }

    // subscribe to onDefaultLangChange event, in case the default language changes
    if (!this.onDefaultLangChange) {
      this.onDefaultLangChange = this.translate.onDefaultLangChange.subscribe((event) => {
        if (this.lastText) {
          this.lastText = null; // we want to make sure it doesn't return the same value until it's been updated
          this.updateValue(text, params, event.lang);
        }
      });
    }

    return this.value;
  }

  /**
   * Clean any existing subscription to change events
   */
  private _dispose(): void {
    if (typeof this.onLangChange !== 'undefined') {
      this.onLangChange.unsubscribe();
      this.onLangChange = undefined;
    }
    if (typeof this.onDefaultLangChange !== 'undefined') {
      this.onDefaultLangChange.unsubscribe();
      this.onDefaultLangChange = undefined;
    }
  }



  ngOnDestroy(): void {
    this._dispose();
  }
}
